import { createFileRoute, Link } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
  Skeleton,
  Checkbox,
} from '@lead-magnifier/ui'
import {
  Download,
  Search,
  Users,
  Check,
  Clock,
  RefreshCw,
  ArrowLeft,
  Mail,
  Calendar,
} from 'lucide-react'
import { useEvent } from '@/hooks/use-events'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'

export const Route = createFileRoute('/_authenticated/events/$eventId/leads')({
  component: EventLeadsPage,
})

interface Lead {
  id: string
  email: string
  name: string | null
  doi_confirmed: boolean
  doi_confirmed_at: string | null
  created_at: string
  custom_fields: Record<string, unknown> | null
}

function EventLeadsPage() {
  const { eventId } = Route.useParams()
  const { user } = useAuth()
  const { data: event, isLoading: eventLoading } = useEvent(eventId)

  const [search, setSearch] = useState('')
  const [showConfirmedOnly, setShowConfirmedOnly] = useState(false)

  // Fetch leads for this specific event
  const { data: leads, isLoading: leadsLoading, refetch, isFetching } = useQuery({
    queryKey: ['event-leads', eventId],
    queryFn: async (): Promise<Lead[]> => {
      const { data, error } = await supabase
        .from('leads')
        .select('*')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!eventId,
    refetchInterval: 10000, // Refresh every 10s for near-realtime
  })

  const filteredLeads = (leads || []).filter((lead) => {
    if (search &&
        !lead.email.toLowerCase().includes(search.toLowerCase()) &&
        !lead.name?.toLowerCase().includes(search.toLowerCase())) {
      return false
    }
    if (showConfirmedOnly && !lead.doi_confirmed) {
      return false
    }
    return true
  })

  const stats = {
    total: leads?.length || 0,
    confirmed: leads?.filter((l) => l.doi_confirmed).length || 0,
    pending: leads?.filter((l) => !l.doi_confirmed).length || 0,
    conversionRate: leads?.length
      ? Math.round((leads.filter((l) => l.doi_confirmed).length / leads.length) * 100)
      : 0,
  }

  const exportCSV = () => {
    if (!event) return
    const headers = ['E-Mail', 'Name', 'Status', 'Bestätigt am', 'Erstellt am']
    const rows = filteredLeads.map((lead) => [
      lead.email,
      lead.name || '',
      lead.doi_confirmed ? 'Bestätigt' : 'Ausstehend',
      lead.doi_confirmed_at ? new Date(lead.doi_confirmed_at).toLocaleString('de-AT') : '',
      new Date(lead.created_at).toLocaleString('de-AT'),
    ])

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n')
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${event.slug}-leads-${new Date().toISOString().split('T')[0]}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'gerade eben'
    if (minutes < 60) return `vor ${minutes} Min`
    if (hours < 24) return `vor ${hours} Std`
    if (days < 7) return `vor ${days} Tagen`
    return date.toLocaleDateString('de-AT')
  }

  const isLoading = eventLoading || leadsLoading

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-[400px]" />
      </div>
    )
  }

  if (!event) {
    return (
      <div className="space-y-6">
        <Link to="/events" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück zu Events
        </Link>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-destructive">Event nicht gefunden.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/events/${eventId}`} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold">Leads: {event.name}</h1>
            <p className="text-sm text-muted-foreground">
              Alle Kontakte für dieses Event
            </p>
          </div>
        </div>
        <Button onClick={exportCSV} disabled={filteredLeads.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          CSV exportieren
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />
              Gesamt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Check className="h-3 w-3 text-green-600" />
              Bestätigt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{stats.confirmed}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3 text-yellow-600" />
              Ausstehend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Conversion</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.conversionRate}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Suche nach E-Mail oder Name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="confirmed"
                checked={showConfirmedOnly}
                onCheckedChange={(checked) => setShowConfirmedOnly(checked === true)}
              />
              <label htmlFor="confirmed" className="text-sm cursor-pointer">
                Nur bestätigte
              </label>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card>
        <CardContent className="p-0">
          {filteredLeads.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Users className="h-12 w-12 text-muted-foreground mb-4" />
              <CardTitle className="mb-2">Keine Leads gefunden</CardTitle>
              <CardDescription>
                {search || showConfirmedOnly
                  ? 'Versuche andere Filterkriterien.'
                  : 'Teile deinen QR-Code, um Leads zu sammeln.'}
              </CardDescription>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Bestätigt</TableHead>
                  <TableHead>Erstellt</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLeads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {lead.email}
                      </div>
                    </TableCell>
                    <TableCell>{lead.name || '-'}</TableCell>
                    <TableCell>
                      {lead.doi_confirmed ? (
                        <Badge variant="default" className="bg-green-600">
                          <Check className="h-3 w-3 mr-1" />
                          Bestätigt
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <Clock className="h-3 w-3 mr-1" />
                          Ausstehend
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {lead.doi_confirmed_at ? (
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(lead.doi_confirmed_at).toLocaleString('de-AT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(lead.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
